import { Box, CircularProgress, makeStyles, styled } from "@material-ui/core";
import { CheckCircle, ErrorOutline } from "@material-ui/icons";
import type { ToolUIPart } from "ai";
import { memo } from "react";
import {
  EPOCUS_BORDER,
  EPOCUS_STATUS_DRAFT_BG,
  EPOCUS_STATUS_DRAFT_TEXT,
  EPOCUS_SYNCED_TEXT,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
  rem,
} from "../../../_lib/epocus-palette";
import type { EpocusProgramRole, OnboardingTools } from "../onboarding-chat";
import { HandoverCard } from "./handover-card";

const Row = styled(Box)({
  display: "flex",
  alignItems: "flex-start",
  gap: 10,
  paddingLeft: 38,
});

const useEntranceStyles = makeStyles({
  "@keyframes settle": {
    "0%": { opacity: 0, transform: "translateY(4px)" },
    "100%": { opacity: 1, transform: "none" },
  },
  entering: {
    animation: "$settle .24s ease-out both",
    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
  },
});

const Label = styled("span")({
  fontSize: rem(13),
  lineHeight: 1.5,
  color: EPOCUS_TEXT_SECONDARY,
});

const DoneLabel = styled("span")({
  fontSize: rem(13),
  lineHeight: 1.5,
  color: EPOCUS_TEXT_PRIMARY,
});

const IconSlot = styled(Box)({
  width: 18,
  height: 18,
  flexShrink: 0,
  marginTop: 2,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const ResultSlot = styled(Box)({
  paddingLeft: 38,
  paddingTop: 4,
});

const SummaryCard = styled(Box)({
  backgroundColor: EPOCUS_WHITE,
  border: `1px solid ${EPOCUS_BORDER}`,
  borderRadius: 12,
  padding: 12,
  fontSize: rem(13),
  color: EPOCUS_TEXT_PRIMARY,
});

const FailureCard = styled(Box)({
  border: `1px solid ${EPOCUS_BORDER}`,
  backgroundColor: EPOCUS_STATUS_DRAFT_BG,
  borderRadius: 12,
  padding: 12,
  fontSize: rem(13),
  lineHeight: 1.5,
  color: EPOCUS_STATUS_DRAFT_TEXT,
});

const ROLE_LABELS: Record<EpocusProgramRole, string> = {
  PERFORMER: "Performer",
  REVIEWER: "Reviewer",
  QA_ADMIN: "QA admin",
  PROGRAM_ADMIN: "Program admin",
};

const describe = (toolName: string, input: unknown): string => {
  const value = (input ?? {}) as Record<string, unknown>;
  switch (toolName) {
    case "epocusGateConfiguration":
      return "Checking that new programs can receive scans";
    case "createEpocusProgram":
      return `Creating ${String(value.name ?? "the program")}`;
    case "provisionEpocusProgramMember":
      return `Adding ${String(value.name ?? "a person")} as a ${
        ROLE_LABELS[value.role as EpocusProgramRole]?.toLowerCase() ?? "member"
      }`;
    case "setEpocusProgramEnabledExamTypes":
      return "Enabling the exam types this site scans";
    case "setEpocusProgramQaSamplingRate":
      return "Setting the QA sampling rate";
    case "sendTestStudy":
      return "Sending a test study through the DIMSE gate";
    case "submit":
      return `Setting up ${String(value.programName ?? "the program")}`;
    default:
      return toolName;
  }
};

const isBlocking = (toolName: string, output: unknown) => {
  if (output == null) return false;
  switch (toolName) {
    case "epocusGateConfiguration":
      return (
        (output as OnboardingTools["epocusGateConfiguration"]["output"])
          .canRouteNewPrograms === false
      );
    case "submit":
      return (output as OnboardingTools["submit"]["output"]).ok === false;
    default:
      return false;
  }
};

const ToolOutputBody: FC<{ toolName: string; output: unknown }> = ({
  toolName,
  output,
}) => {
  if (output == null) return null;
  const value = output as Record<string, unknown>;

  switch (toolName) {
    case "createEpocusProgram":
      return (
        <HandoverCard
          name={String(value.name)}
          code={String(value.dimseAeTitle)}
        />
      );
    case "provisionEpocusProgramMember":
      return (
        <SummaryCard>
          {`${String(value.name)} · ${String(value.email)} · ${
            ROLE_LABELS[value.role as EpocusProgramRole] ?? String(value.role)
          }`}
        </SummaryCard>
      );
    case "setEpocusProgramEnabledExamTypes":
      return Array.isArray(value.examTypes) ? (
        <SummaryCard>{value.examTypes.join(", ")}</SummaryCard>
      ) : null;
    case "setEpocusProgramQaSamplingRate":
      return (
        <SummaryCard>{`${String(value.percent)}% of exams sampled`}</SummaryCard>
      );
    case "epocusGateConfiguration":
      return value.canRouteNewPrograms ? null : (
        <FailureCard>
          No ingress gates are configured, so a new program would be given an AE
          title it can never receive a scan on.
        </FailureCard>
      );
    default:
      return null;
  }
};

export const ToolActivity = memo<{
  toolName: string;
  title?: string;
  state: ToolUIPart["state"];
  input: unknown;
  output: unknown;
  errorText?: string;
}>(({ toolName, title, state, input, output, errorText }) => {
  const running = state === "input-streaming" || state === "input-available";
  const failed = state === "output-error" || state === "output-denied";
  const blocked = isBlocking(toolName, output);
  const label = title ?? describe(toolName, input);
  const entrance = useEntranceStyles();

  return (
    <Box className={entrance.entering}>
      <Row>
        <IconSlot>
          {running && <CircularProgress size={14} thickness={5} />}
          {state === "output-available" && !blocked && (
            <CheckCircle style={{ fontSize: 18, color: EPOCUS_SYNCED_TEXT }} />
          )}
          {(failed || blocked) && (
            <ErrorOutline
              style={{ fontSize: 18, color: EPOCUS_STATUS_DRAFT_TEXT }}
            />
          )}
        </IconSlot>
        {running ? (
          <Label>{`${label}…`}</Label>
        ) : (
          <DoneLabel>{label}</DoneLabel>
        )}
      </Row>
      {state === "output-available" && (
        <ResultSlot>
          <ToolOutputBody toolName={toolName} output={output} />
        </ResultSlot>
      )}
      {errorText && (
        <ResultSlot>
          <FailureCard>{errorText}</FailureCard>
        </ResultSlot>
      )}
    </Box>
  );
});
ToolActivity.displayName = "ToolActivity";
